/**
 * Unified HTTP client (Phase-3 batch-3 consolidation of api/api.js + api/auth.js).
 *
 * Exports:
 *   default `api`  — baseURL ${REACT_APP_BACKEND_URL}/api, 2-min timeout.
 *   `aiApi`        — same, 10-min timeout + retry on network/timeout errors.
 *
 * Both instances share:
 *   - request interceptor: Bearer token from localStorage.accessToken
 *   - response interceptor:
 *       • 401 -> clear auth storage + redirect to /login (org-slug aware),
 *         EXCEPT on auth pages or for auth endpoints (login, lookup, OTP,
 *         forgot/reset password, OAuth callback) so those show their own error.
 *       • `detail` normaliser: flattens FastAPI structured details
 *         (RBAC objects, Pydantic 422 arrays) to a plain string on
 *         error.message + error.normalizedDetail for display. data.detail is
 *         left RAW so structured readers (detail.organizations, conflictingOrg,
 *         422 arrays) keep working; a mirror string is also on data.detailRaw.
 *         Render code must use error.message, never data.detail.
 */

import axios from 'axios';

const API_BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Shared helpers for the few raw fetch()/download call sites that can't use the
// axios instance (blob streaming etc.) — they read the base URL + token here
// instead of re-deriving them from env/localStorage.
export const getApiBaseUrl = () => API_BASE_URL;
export const getAuthToken = () => localStorage.getItem('accessToken');

// Pages where a 401 must NOT trigger a logout redirect (the page shows its own error).
const AUTH_EXEMPT_PATH_SUFFIXES = [
  '/login', '/register', '/self-onboard',
  '/forgot-password', '/reset-password', '/auth/callback',
];

// Request URLs (relative to baseURL) that must NOT trigger a logout redirect.
const AUTH_EXEMPT_ENDPOINTS = [
  '/auth/login', '/auth/lookup', '/auth/otp', '/auth/password',
  '/auth/callback', '/auth/oauth', '/auth/register', '/auth/self-onboard',
  '/auth/forgot-password', '/auth/reset-password',
];

// True when the FAILED request itself targeted an auth endpoint. These calls
// must receive the RAW backend response: no 401 redirect and — critically — NO
// `detail` normalising, because the backend's structured responses carry data
// the pages need (e.g. the HTTP-300 "multiple organizations" picker list in
// data.detail.organizations). Flattening detail to a string broke the org
// picker after batch-3; this restores exact pre-batch-3 behaviour.
function isAuthEndpointRequest(error) {
  try {
    const url = error?.config?.url || '';
    return AUTH_EXEMPT_ENDPOINTS.some((e) => url.includes(e));
  } catch (e) {
    return false;
  }
}

function isAuthExempt(error) {
  try {
    const path = window.location.pathname || '';
    const onAuthPage = AUTH_EXEMPT_PATH_SUFFIXES.some(
      (p) => path === p || path.endsWith(p)
    );
    return onAuthPage || isAuthEndpointRequest(error);
  } catch (e) {
    return false;
  }
}

// Pre-batch-3 behaviour is now the default for ALL calls: normaliseError
// (below) never overwrites data.detail, so auth responses keep their raw
// structured payload (e.g. the 300 multi-org picker list). handle401 still
// skips the logout redirect for auth pages/endpoints via isAuthExempt.

function handle401(error) {
  if (error?.response?.status === 401 && !isAuthExempt(error)) {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('user');
    localStorage.removeItem('organization');
    const orgSlug = localStorage.getItem('currentOrgSlug');
    window.location.href = orgSlug ? `/${orgSlug}/login` : '/login';
  }
}

function normaliseError(error) {
  try {
    const data = error?.response?.data;
    if (!data) return;
    if (data.detail !== undefined && data.detail !== null) {
      const d = data.detail;
      let text;
      if (typeof d === 'string') {
        text = d;
      } else if (Array.isArray(d)) {
        // Pydantic 422 array -> join the `msg` fields.
        text = d
          .map((entry) =>
            entry && typeof entry === 'object'
              ? entry.msg || entry.message || JSON.stringify(entry)
              : String(entry)
          )
          .filter(Boolean)
          .join('; ');
      } else if (typeof d === 'object') {
        // Structured detail (e.g. RBAC perm_denied).
        text = d.message || d.msg || d.detail || JSON.stringify(d);
      } else {
        text = String(d);
      }
      // IMPORTANT: data.detail is left RAW (never overwritten) so structured
      // readers keep working — e.g. detail.organizations (300 multi-org picker),
      // detail.conflictingOrg (409 org rename), Pydantic 422 arrays, RBAC
      // objects. The flattened string goes on error.message + normalizedDetail;
      // render code must use error.message, never data.detail.
      data.detailRaw = d; // flattened-source mirror for advanced callers
      error.normalizedDetail = text; // flat display string
      error.message = text; // primary display source
    } else {
      error.message = 'An error occurred';
    }
  } catch (e) {
    // Never let the normaliser itself crash the error path.
  }
}

function attachAuthHeader(config) {
  const token = localStorage.getItem('accessToken');
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
}

// ─── default api (2-min timeout) ──────────────────────────────────────────
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 120000,
});

api.interceptors.request.use(attachAuthHeader, (error) => Promise.reject(error));
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // handle401 skips the logout redirect on auth pages / auth endpoints.
    // normaliseError never overwrites data.detail, so auth flows (300 picker,
    // etc.) and structured readers all receive the raw payload.
    handle401(error);
    normaliseError(error);
    return Promise.reject(error);
  }
);

// ─── aiApi (10-min timeout + retry) ───────────────────────────────────────
export const aiApi = axios.create({
  baseURL: API_BASE_URL,
  headers: { 'Content-Type': 'application/json' },
  timeout: 600000,
});

aiApi.interceptors.request.use(attachAuthHeader, (error) => Promise.reject(error));
aiApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Cancellations are expected when a user stops generation — surface as-is.
    if (error.name === 'CanceledError' || error.message === 'canceled') {
      return Promise.reject(error);
    }
    // Retry transient network/timeout errors (up to 2x, exponential backoff).
    const cfg = error.config || {};
    const retriable = error.code === 'ECONNABORTED' || error.message === 'Network Error';
    if (retriable && (cfg.__retryCount || 0) < 2) {
      cfg.__retryCount = (cfg.__retryCount || 0) + 1;
      const delay = 2000 * Math.pow(2, cfg.__retryCount - 1);
      await new Promise((resolve) => setTimeout(resolve, delay));
      return aiApi(cfg);
    }
    // Auth calls: handle401 already skips redirect for them; data.detail stays raw.
    handle401(error);
    normaliseError(error);
    return Promise.reject(error);
  }
);

export default api;
